/**
 * Devluxx — Last Add-to-Cart tracker (v2)
 *
 * Priority for the empty-cart "Start Shopping" button:
 *   1. Last product added to cart
 *   2. Last product page viewed
 *   3. /collections/all
 *
 * Debug: add ?dlxdebug to any URL, or run window.DLX_ATC_DEBUG = true
 *
 * Install:
 *   1) assets/last-atc.js
 *   2) theme.liquid <head>:  <script src="{{ 'last-atc.js' | asset_url }}" defer="defer"></script>
 *   3) data-start-shopping on the empty-cart button
 */
(function () {
  'use strict';

  var ATC_KEY = 'dlx_last_atc';
  var PDP_KEY = 'dlx_last_pdp';
  var TTL = 7 * 24 * 60 * 60 * 1000;
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var FALLBACK = ROOT.replace(/\/$/, '') + '/collections/all';

  // Handles to ignore (e.g. shipping protection / hidden upsell products)
  var SKIP_HANDLES = [];

  var DEBUG = /[?&]dlxdebug/.test(window.location.search) || window.DLX_ATC_DEBUG === true;

  function log() {
    if (!DEBUG) return;
    var args = ['%c[DLX-ATC]', 'color:#fff;background:#5b21b6;padding:2px 5px;border-radius:3px'];
    console.log.apply(console, args.concat([].slice.call(arguments)));
  }

  /* ---------- storage: localStorage, cookie fallback ---------- */

  function write(key, val) {
    try {
      localStorage.setItem(key, val);
      return 'localStorage';
    } catch (e) {
      log('localStorage blocked:', e && e.message);
    }
    try {
      document.cookie = key + '=' + encodeURIComponent(val) + ';path=/;max-age=' + TTL / 1000 + ';SameSite=Lax';
      return 'cookie';
    } catch (e2) {
      log('cookie blocked too:', e2 && e2.message);
    }
    return null;
  }

  function read(key) {
    try {
      var v = localStorage.getItem(key);
      if (v) return v;
    } catch (e) {}
    var m = document.cookie.match('(?:^|; )' + key + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }

  function remove(key) {
    try {
      localStorage.removeItem(key);
    } catch (e) {}
    document.cookie = key + '=;path=/;max-age=0';
  }

  /* ---------- save / get ---------- */

  function save(key, url, source) {
    if (!url) {
      log('save skipped — no url', '(' + source + ')');
      return;
    }
    try {
      var u = new URL(url, window.location.origin);

      if (u.pathname.indexOf('/products/') === -1) {
        log('save skipped — not a product url:', u.pathname, '(' + source + ')');
        return;
      }

      var handle = u.pathname.split('/products/')[1].split(/[?#/]/)[0];
      if (SKIP_HANDLES.indexOf(handle) !== -1) {
        log('save skipped — handle in skip list:', handle);
        return;
      }

      var variant = u.searchParams.get('variant');
      var clean = u.pathname + (variant ? '?variant=' + variant : '');

      var where = write(key, JSON.stringify({ url: clean, t: Date.now() }));
      log('SAVED', key, '=', clean, '| via', source, '| storage:', where || 'FAILED');
    } catch (e) {
      log('save error:', e && e.message);
    }
  }

  function get(key) {
    var raw = read(key);
    if (!raw) return null;
    try {
      var data = JSON.parse(raw);
      if (!data || !data.url) return null;
      if (Date.now() - data.t > TTL) {
        remove(key);
        return null;
      }
      return data.url;
    } catch (e) {
      return null;
    }
  }

  /* ---------- tier 2: remember the product page being viewed ---------- */

  if (window.location.pathname.indexOf('/products/') !== -1) {
    save(PDP_KEY, window.location.pathname + window.location.search, 'page view');
  }

  /* ---------- 1) fetch hook ---------- */

  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function () {
      var target = arguments[0];
      var url = typeof target === 'string' ? target : (target && target.url) || '';
      var isAdd = url.indexOf('/cart/add') !== -1;

      if (DEBUG && url.indexOf('/cart') !== -1) log('fetch →', url, isAdd ? '(ATC)' : '');

      return origFetch.apply(this, arguments).then(function (res) {
        if (isAdd && res && res.ok) {
          res
            .clone()
            .json()
            .then(function (data) {
              log('ATC response:', data);
              save(ATC_KEY, data.url || (data.items && data.items[0] && data.items[0].url), 'fetch');
            })
            .catch(function (e) {
              log('could not parse ATC response:', e && e.message);
            });
        }
        return res;
      });
    };
    log('fetch patched');
  }

  /* ---------- 2) XHR hook ---------- */

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__dlxCartAdd = typeof url === 'string' && url.indexOf('/cart/add') !== -1;
    if (DEBUG && this.__dlxCartAdd) log('XHR →', url);
    return origOpen.apply(this, arguments);
  };

  var origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__dlxCartAdd) {
      this.addEventListener('load', function () {
        try {
          var data = JSON.parse(this.responseText);
          log('ATC response (XHR):', data);
          save(ATC_KEY, data.url || (data.items && data.items[0] && data.items[0].url), 'xhr');
        } catch (e) {}
      });
    }
    return origSend.apply(this, arguments);
  };

  /* ---------- 3) click + submit fallback ---------- */

  function urlFromForm(form) {
    if (!form) return '';
    if (form.dataset && form.dataset.productUrl) return form.dataset.productUrl;

    var card = form.closest('.card-wrapper, .card, .product-card-wrapper, .grid__item, li');
    var link = card && card.querySelector('a[href*="/products/"]');
    if (link) return link.getAttribute('href');

    if (window.location.pathname.indexOf('/products/') !== -1) {
      return window.location.pathname + window.location.search;
    }
    return '';
  }

  document.addEventListener(
    'click',
    function (e) {
      if (!e.target.closest) return;
      var btn = e.target.closest(
        '[name="add"], .product-form__submit, .quick-add__submit, [data-add-to-cart], button[type="submit"]'
      );
      if (!btn) return;

      var form = btn.closest('form[action*="/cart/add"]') || btn.closest('product-form');
      var onPdp = window.location.pathname.indexOf('/products/') !== -1;

      // A plain submit button with no cart/add form and not on a PDP isn't an ATC
      if (!form && !(onPdp && (btn.name === 'add' || btn.classList.contains('product-form__submit')))) {
        return;
      }

      log('ATC click detected on:', btn.className || btn.name, '| form found:', !!form);

      var url = form ? urlFromForm(form) : '';
      if (!url && onPdp) url = window.location.pathname + window.location.search;

      save(ATC_KEY, url, 'click');
    },
    true
  );

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      log('ATC form submit detected');
      save(ATC_KEY, urlFromForm(form), 'submit');
    },
    true
  );

  /* ---------- 4) Start Shopping ---------- */

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var el = e.target.closest('[data-start-shopping]');
    if (!el) return;

    e.preventDefault();
    var dest = get(ATC_KEY) || get(PDP_KEY) || FALLBACK;
    log('Start Shopping →', dest, '| atc:', get(ATC_KEY), '| pdp:', get(PDP_KEY));
    window.location.href = dest;
  });

  /* ---------- console helpers ---------- */

  window.DLXLastATC = {
    atc: function () {
      return get(ATC_KEY);
    },
    pdp: function () {
      return get(PDP_KEY);
    },
    save: function (url) {
      save(ATC_KEY, url, 'manual');
    },
    clear: function () {
      remove(ATC_KEY);
      remove(PDP_KEY);
    },
    debug: function () {
      DEBUG = true;
      log('debug on');
    },
    storageOK: function () {
      try {
        localStorage.setItem('dlx_test', '1');
        localStorage.removeItem('dlx_test');
        return true;
      } catch (e) {
        return false;
      }
    }
  };

  log('ready | root:', ROOT, '| fallback:', FALLBACK);
})();